import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {SelectModule} from 'primeng/select';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {ServerTranslateService} from '@services/server-translate.service';
import {getDeepField, userNames} from '@app/utils/_msf';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FilterOperator} from '@app/utils/filter-operator';
import {BaseService} from '@6grain/masofa-monolith-api-client/common/api.base.service';
import {IBaseGetQuery} from '@models/IBaseQuery';

export interface PaginatedSelectConfig {
  pageSize: number;
  searchDebounceMs: number;
}

const DEFAULT_CONFIG: PaginatedSelectConfig = {
  pageSize: 25,
  searchDebounceMs: 300
};

@Component({
  selector: 'msf-select-paginated',
  exportAs: 'msfSelectPaginated',
  imports: [
    SelectModule,
    FormsModule,
    TranslatePipe,
  ],
  templateUrl: './select-paginated.html',
  styleUrls: [
    './select-paginated.scss',
    '../../dropdown/dropdown.component.scss'
  ],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MsfSelectPaginated),
      multi: true
    }
  ],
})
export class MsfSelectPaginated implements OnInit, OnDestroy, ControlValueAccessor {
  private $destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  // State
  orderedOptions: any[] = [];
  loading = false;
  loadingMore = false;
  hasMore = true;
  currentPage = 0;
  searchTerm: any = '';
  totalLoaded = 0;

  // Callbacks for ControlValueAccessor
  private onTouchedCallback: () => void = () => {
  };
  private onChangeCallback: (_: any) => void = () => {
  };
  private innerValue: any;

  private _filter: Array<{ filterField: string, filterOperator: FilterOperator, filterValue: any }> = [];

  constructor(
    private serverTranslateService: ServerTranslateService,
    private translateService: TranslateService,
  ) {
  }

  // Inputs
  @Input() options?: any[] = [];
  @Input() model: any;
  @Output() modelChange = new EventEmitter<any>();
  @Output() selectItem = new EventEmitter<any>();
  @Output() onLoadSelectItem = new EventEmitter<any>();
  @Input() placeholder: string = 'Выберите...';
  @Input() optionLabel: string = 'label';
  @Input() optionSubtitle!: string;
  @Input() translateBy: string | null = 'names';
  @Input() searchBy: string | null = 'names';
  @Input() searchKeywords: string[] | null = ['names'];
  @Input() optionValue?: string = 'id';
  @Input() service?: BaseService | any;
  @Input() serviceMethod?: string;
  @Input() showClear = false;
  @Input() disabled = false;

  @Input() width = '100%';
  @Input() isUser = false;
  @Input() config: Partial<PaginatedSelectConfig> = {};


  @Input() set filter(value: Array<{ filterField: string, filterOperator: FilterOperator, filterValue: any }>) {
    if (value.length === 0) {
      this._filter = [];
      return;
    }
    this._filter = value;

    if (this.service && this.serviceMethod) {
      this.resetAndSearch();
    }
  }

  /**
   * Reload trigger: pass a new number/timestamp (e.g. Date.now()) to force
   * resetAndSearch() from the parent without destroying/recreating the component.
   */
  @Input() set reload(value: number | boolean | undefined) {
    if (value && this.service && this.serviceMethod) {
      this.resetAndSearch();
    }
  }
  // Merged config
  private get mergedConfig(): PaginatedSelectConfig {
    return {...DEFAULT_CONFIG, ...this.config};
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.innerValue = value;
    this.model = value;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit() {
    this.setupSearchDebounce();
    this.loadInitialData();

    // React to language changes
    this.translateService.onLangChange
      .pipe(takeUntil(this.$destroy$))
      .subscribe(() => {
        this.orderedOptions = this.translateOptions(this.orderedOptions);
      });
  }

  ngOnDestroy() {
    this.$destroy$.next();
    this.$destroy$.complete();
  }

  /**
   * Setup search with debounce
   */
  private setupSearchDebounce(): void {
    this.searchSubject$
      .pipe(
        debounceTime(this.mergedConfig.searchDebounceMs),
        distinctUntilChanged(),
        takeUntil(this.$destroy$)
      )
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;
        this.resetAndSearch();
      });
  }

  /**
   * Load initial data (first page)
   */
  private loadInitialData(): void {
    if (!this.service || !this.serviceMethod) {
      // If no service, use provided options
      this.orderedOptions = this.translateOptions(this.options || []);
      this.loadSelectedItemIfMissing();
      return;
    }

    this.loading = true;
    this.currentPage = 0;
    this.loadPage().then(() => {
      this.loadSelectedItemIfMissing();
    });
  }

  /**
   * Load selected item if it's not in the current list
   */
  private loadSelectedItemIfMissing(): void {
    if (!this.model || !this.service || !this.serviceMethod || !this.optionValue) {
      return;
    }

    // Check if model already exists in loaded options
    const existingItem = this.orderedOptions.find(
      item => getDeepField(item, this.optionValue as string) === this.model
    );

    if (existingItem) {
      this.onLoadSelectItem.emit(existingItem);
      return; // Item already in list, no need to load
    }

    // Load the selected item by its ID
    const params: any = {
      take: 1,
      offset: 0,
      filters: [
        {
          ...this._filter,
          filterField: this.optionValue,
          filterOperator: FilterOperator.Equals,
          filterValue: this.model
        }
      ]
    };

    this.loading = true;
    this.service[this.serviceMethod](params)
      .pipe(takeUntil(this.$destroy$))
      .subscribe({
        next: (response: any) => {
          this.loading = false
          const items = this.extractItems(response);
          if (items.length > 0) {
            const translatedItems = this.translateOptions(items);
            // Add the selected item at the beginning of the list

            if (translatedItems.length > 0) {
              this.onLoadSelectItem.emit(translatedItems[0]);
            }
            this.orderedOptions = [...translatedItems, ...this.orderedOptions];
          }
        },
        error: () => {
          // Silently fail - item might not exist
        }
      });
  }

  /**
   * Reset pagination and search
   */
  private resetAndSearch(): void {
    this.currentPage = 0;
    this.orderedOptions = [];
    this.hasMore = true;
    this.totalLoaded = 0;
    this.loadPage();
  }

  /**
   * Load a page of data
   */
  private loadPage(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.service || !this.serviceMethod) {
        resolve();
        return;
      }

      const isFirstPage = this.currentPage === 0;
      if (isFirstPage) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const requestParams = this.buildRequestParams();

      this.service[this.serviceMethod](requestParams)
        .pipe(takeUntil(this.$destroy$))
        .subscribe({
          next: (response: any) => {

            const items = this.extractItems(response);
            const translatedItems = this.translateOptions(items);
            if (isFirstPage) {
              this.orderedOptions = translatedItems;
            } else {
              this.orderedOptions = [...this.orderedOptions, ...translatedItems];
            }

            this.totalLoaded += items.length;
            this.hasMore = items.length >= this.mergedConfig.pageSize;
            this.loading = false;
            this.loadingMore = false;
            resolve();
          },
          error: () => {
            this.loading = false;
            this.loadingMore = false;
            resolve();
          }
        });
    });
  }

  /**
   * Build request parameters for API call
   */
  private buildRequestParams(): any {
    const params: IBaseGetQuery = {
      filters: this._filter,
      take: this.mergedConfig.pageSize,
      offset: this.currentPage * this.mergedConfig.pageSize,
      // selectFields: [this.optionValue as string, this.optionLabel, this.optionSubtitle],
    };

    // Add search filter if search term exists
    if (this.searchTerm && !this.searchTerm?.originalEvent) {

      if (this.searchKeywords) {
        let searchFilters: any [] = []
        this.searchKeywords.forEach(keyword => {
          searchFilters.push({
            filterField: keyword,
            filterOperator: FilterOperator.Contains, // Contains
            filterValue: this.searchTerm
          })
        })

        params.filters = [
          ...(params.filters || []),
          {
            filterField: this.searchBy,
            filterOperator: FilterOperator.Contains, // Contains
            filterValue: this.searchTerm
          }
        ];
      }else {
        params.filters = [
          ...(params.filters || []),
          {
            filterField: this.searchBy,
            filterOperator: FilterOperator.Contains, // Contains
            filterValue: this.searchTerm
          }
        ];
      }

    }

    return params;
  }

  /**
   * Extract items from API response (handles both array and paginated response)
   */
  private extractItems(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (response?.items && Array.isArray(response.items)) {
      return response.items;
    }
    if (response?.data && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  /**
   * Translate options labels
   */
  private translateOptions(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    return items
      .map((item: any) => {
        let labelValue: any;

        if (this.isUser) {
          labelValue = userNames(item);
        } else if (this.translateBy === 'locale') {
          labelValue = this.translateService.instant(item[this.optionLabel]);
        } else if (this.translateBy && this.translateBy !== 'none') {
          labelValue = this.serverTranslateService.translate(item, this.translateBy);
        } else {
          labelValue = getDeepField(item, this.optionLabel);
        }

        item['label'] = this.serverTranslateService.formatTranslationLabel(labelValue);

        if (this.optionSubtitle) {

          const subtitleValue = getDeepField(item, this.optionSubtitle);
          item['subtitle'] = this.serverTranslateService.formatTranslationLabel(subtitleValue);
        }
        return item;
      })
      .filter((item: any) => {
        const hasValidLabel = this.serverTranslateService.isValidDisplayLabel(item.label);
        const namesNull = this.translateBy === 'names' && item.names
          && this.serverTranslateService.isEmptyTranslationValue(item.names);
        return hasValidLabel && !namesNull;
      }).sort((a, b) => {
        const labelA = a?.label?.toString()?.toLowerCase();
        const labelB = b?.label?.toString()?.toLowerCase();
        return labelA.localeCompare(labelB);
      });
  }

  /**
   * Handle search input
   */
  onSearch(event: any): void {
    const searchTerm = event.filter || event.query || event || '';
    this.searchSubject$.next(searchTerm);
  }

  /**
   * Handle scroll to load more (infinite scroll)
   */
  onLazyLoad(event: any): void {
    if (this.loadingMore || !this.hasMore) return;

    // Check if we're near the bottom
    const {first, last} = event;
    const threshold = this.orderedOptions.length - 5;

    if (last >= threshold && this.hasMore) {
      this.currentPage++;
      this.loadPage();
    }
  }

  /**
   * Handle dropdown scroll event
   */
  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;
    const threshold = 50; // pixels from bottom

    if (scrollHeight - scrollPosition <= threshold && !this.loadingMore && this.hasMore) {
      this.currentPage++;
      this.loadPage();
    }
  }

  /**
   * Handle item selection
   */
  onSelectItem(): void {
    const item = this.orderedOptions?.find(
      i => getDeepField(i, this.optionValue as string) === this.model
    );
    this.selectItem.emit(item);
    this.onChangeCallback(this.model);
    this.modelChange.emit(this.model);
  }

  /**
   * Handle dropdown open
   */
  onShow(): void {
    // Refresh data when dropdown opens if needed
    if (this.orderedOptions.length === 0) {
      this.loadInitialData();
    }
  }

  /**
   * Handle clear selection
   */
  onClear(): void {
    this.model = null;
    this.modelChange.emit(null);
    this.onChangeCallback(null);
    this.selectItem.emit(null);
  }
}
